import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { generateUuid } from '@ftr/contracts/type/shared'
import { BasicCheckboxComponent, CheckboxItem, TristateValue, getTristateValue } from '@ftr/forms'
import { ComposableDropdownModule, ComposableDropdownRenderMode, IconComponent } from '@ftr/foundation'
import { BehaviorSubject, Observable, combineLatest, debounceTime, distinctUntilChanged, map, tap } from 'rxjs'

/**
 * Wait for the user to stop typing before applying the filter.
 *
 * Improves performance of the dropdown
 */
const FILTER_DEBOUNCE = 100

export interface CheckboxListDropdownViewModel {
  filteredItems: CheckboxItem[]
  hasFilter: boolean
  totalFilterCount: number
}

@Component({
  selector: 'ftr-checkbox-list-dropdown',
  templateUrl: './checkbox-list-dropdown.component.html',
  styleUrls: ['./checkbox-list-dropdown.component.css'],
  standalone: true,
  imports: [BasicCheckboxComponent, CommonModule, ComposableDropdownModule, FormsModule, IconComponent],
})
export class CheckboxListDropdownComponent implements OnInit {
  @Input() items: Observable<CheckboxItem[]>
  /**
   * Provide a fixed header that appears at the top of the list. Will appear when there is at least 1 list item
   */
  @Input() listHeaderTemplate: TemplateRef<unknown> | null = null

  @Input() renderType: ComposableDropdownRenderMode = 'drop-down'

  // Allows us to display "Select all filtered xyzs" where xyzs might be "locations", "users", "gremlins" etc
  @Input() pluralNoun: string = 'items'

  @Input() searchPlaceholder: string = 'Search'

  @Output() valueChange = new EventEmitter<readonly [CheckboxItem, boolean]>()
  @Output() searchTermChange = new EventEmitter<string>()

  @Input() selectedCheckboxes = 0
  @Input() enableSelectAll: boolean = true
  @Input() enableSelectFiltered: boolean = true
  @Input() maxSelectedCheckboxes: number
  @Input() dataTrack = false

  readonly filter = new BehaviorSubject<string>('')

  /*
   * Keeps track of the number of items to show in the dropdown list. This is a hack to get
   * around the fact that ComposableDropdownBody must exist in the DOM ASAP (so no ngIf)
   * due to the ContentChild in ComposableDropdown and the logic surrounding it. I
   */
  readonly totalFilterCount = new BehaviorSubject(0)

  public searchInputId = generateUuid()

  viewModel: Observable<CheckboxListDropdownViewModel>

  ngOnInit(): void {
    this.viewModel = combineLatest([this.items, this.getDebouncedFilter()]).pipe(
      map(([items, filter]) => {
        const hasFilter = filter !== ''
        // Filtered items include checkboxes that are selected
        const filteredItems = hasFilter ? items.map(i => i.withFilterAndSelected(filter)) : items
        const totalFilterCount = filteredItems.filter(item => !item.hidden).length
        this.totalFilterCount.next(totalFilterCount)
        return {
          hasFilter,
          filteredItems,
          totalFilterCount,
        }
      }),
    )
  }

  private getDebouncedFilter(): Observable<string> {
    return this.filter.pipe(
      debounceTime(FILTER_DEBOUNCE),
      map(filter => filter.toLowerCase().trim()),
      distinctUntilChanged(),
      tap(f => this.searchTermChange.emit(f)),
    )
  }

  onSelectionChange(value: boolean, item: CheckboxItem): void {
    this.valueChange.emit([item, value])
  }

  getSelectAllValue(items: CheckboxItem[]): TristateValue {
    const selectedItems = items.filter(x => x.selected)
    return getTristateValue(selectedItems.length, items.length)
  }

  getSelectFilteredValue(items: CheckboxItem[]): TristateValue {
    const filteredItems = items.filter(x => !x.hidden)
    const selectedFilteredItems = filteredItems.filter(x => x.selected)
    return getTristateValue(selectedFilteredItems.length, filteredItems.length)
  }

  selectAll(items: CheckboxItem[], value: boolean): void {
    items.forEach(item => this.onSelectionChange(value, item))
  }

  selectAllFiltered(items: CheckboxItem[], value: boolean): void {
    items.filter(x => !x.hidden).forEach(item => this.onSelectionChange(value, item))
  }

  trackById(_: number, thing: { id: string }): string {
    return thing.id
  }
}
